import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Stack, TextField } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import productoService, { OPTIONS_PRODUCTO_OPERATIVO } from '../../services/productoService';
import { ChequeTerceroRechazoRecargo } from '../../services/tesoreriaService';
import { esTipoRecargoPermitido, debeResolverCodigoArticulo } from '../../utils/chequeTerceroRechazo';
import { obtenerPrecioBaseProducto } from '../../utils/precioListaPedido';
import RecetaArticuloCodigoField from '../../pages/produccion/RecetaArticuloCodigoField';
import RecetaArticuloSearchDialog, {
  RecetaArticuloSeleccionado,
} from '../../pages/produccion/RecetaArticuloSearchDialog';

const TIPOS_RECARGO = 'FIN,SER';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value ?? 0);

interface Props {
  recargo: ChequeTerceroRechazoRecargo;
  onChange: (recargo: ChequeTerceroRechazoRecargo) => void;
  onRemove: () => void;
}

const ChequeTerceroRechazoRecargoRow: React.FC<Props> = ({ recargo, onChange, onRemove }) => {
  const [codigoInput, setCodigoInput] = useState(recargo.codigoArticulo);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const applyRequestIdRef = useRef(0);

  useEffect(() => {
    setCodigoInput(recargo.codigoArticulo);
  }, [recargo.codigoArticulo]);

  const articuloResuelto =
    recargo.codigoArticulo.trim().length > 0 &&
    recargo.descripcion.trim().length > 0 &&
    codigoInput.trim().toUpperCase() === recargo.codigoArticulo.trim().toUpperCase();

  const aplicarProducto = useCallback(
    async (codigo: string) => {
      const trimmed = codigo.trim();
      const requestId = ++applyRequestIdRef.current;

      if (!trimmed) {
        onChange({ codigoArticulo: '', descripcion: '', importe: 0, precioCatalogo: undefined });
        setError(null);
        return;
      }

      setResolving(true);
      setError(null);
      try {
        const producto = await productoService.getProducto(trimmed, OPTIONS_PRODUCTO_OPERATIVO);
        if (requestId !== applyRequestIdRef.current) return;

        if (!esTipoRecargoPermitido(producto.tipoArticulo)) {
          setError('El artículo debe ser Financiero (FIN) o Servicio (SER).');
          return;
        }

        const precioNeto = obtenerPrecioBaseProducto(producto);
        onChange({
          codigoArticulo: producto.codigoArticulo.trim(),
          descripcion: String(producto.descripcion ?? '').trim(),
          importe: precioNeto,
          precioCatalogo: precioNeto,
        });
        setCodigoInput(producto.codigoArticulo.trim());
      } catch {
        if (requestId !== applyRequestIdRef.current) return;
        setError(`No se encontró el artículo ${trimmed}.`);
      } finally {
        if (requestId === applyRequestIdRef.current) setResolving(false);
      }
    },
    [onChange]
  );

  const handleSelect = (item: RecetaArticuloSeleccionado) => {
    applyRequestIdRef.current += 1;
    setError(null);
    setCodigoInput(item.codigoArticulo);
    void aplicarProducto(item.codigoArticulo);
  };

  const handleResolveExact = (codigo: string) => {
    const trimmed = codigo.trim();
    if (!debeResolverCodigoArticulo(trimmed)) return;
    if (
      recargo.codigoArticulo.trim().length > 0 &&
      trimmed.toUpperCase() === recargo.codigoArticulo.trim().toUpperCase()
    ) {
      return;
    }
    void aplicarProducto(trimmed);
  };

  const importeHelper =
    recargo.precioCatalogo != null && recargo.precioCatalogo > 0
      ? `Precio catálogo (neto): ${formatCurrency(recargo.precioCatalogo)}`
      : recargo.codigoArticulo
        ? 'Sin precio de catálogo configurado'
        : undefined;

  return (
    <Stack spacing={0.5} sx={{ mb: 1.5 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems="flex-start"
        sx={{ width: '100%' }}
      >
        <Box sx={{ width: { xs: '100%', sm: 120 }, flexShrink: 0 }}>
          <RecetaArticuloCodigoField
            label="Código"
            size="small"
            value={codigoInput}
            articuloResuelto={articuloResuelto}
            resolving={resolving}
            error={Boolean(error)}
            helperText={error || ' '}
            tiposArticulo={TIPOS_RECARGO}
            modoPrecio="venta"
            sx={{ m: 0, width: '100%', '& input': { fontFamily: 'monospace' } }}
            onChange={(raw) => {
              setError(null);
              setCodigoInput(raw);
              if (
                articuloResuelto &&
                raw.trim().toUpperCase() !== recargo.codigoArticulo.trim().toUpperCase()
              ) {
                onChange({ codigoArticulo: '', descripcion: '', importe: 0, precioCatalogo: undefined });
              }
            }}
            onSelect={handleSelect}
            onResolveExact={handleResolveExact}
            onOpenDialog={() => setDialogOpen(true)}
          />
        </Box>
        <TextField
          size="small"
          label="Descripción"
          value={recargo.descripcion}
          disabled
          multiline
          minRows={1}
          maxRows={2}
          fullWidth
          title={recargo.descripcion}
          sx={{ flex: 1, minWidth: 0 }}
          helperText=" "
          InputProps={{
            sx: {
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: 'text.primary',
                opacity: 0.87,
              },
            },
          }}
        />
        <TextField
          size="small"
          label="Importe neto"
          type="number"
          sx={{ width: { xs: '100%', sm: 130 }, flexShrink: 0 }}
          value={recargo.importe || ''}
          helperText={importeHelper}
          onChange={(e) => {
            const n = parseFloat(e.target.value.replace(',', '.'));
            onChange({
              ...recargo,
              importe: Number.isFinite(n) ? n : 0,
            });
          }}
        />
        <IconButton aria-label="Eliminar recargo" onClick={onRemove} sx={{ mt: { xs: 0, sm: 0.5 }, flexShrink: 0 }}>
          <DeleteIcon />
        </IconButton>
      </Stack>

      <RecetaArticuloSearchDialog
        open={dialogOpen}
        title="Buscar artículo (FIN / SER)"
        tiposArticulo={TIPOS_RECARGO}
        modoPrecio="venta"
        initialSearchTerm={codigoInput}
        onClose={() => setDialogOpen(false)}
        onSelect={handleSelect}
      />
    </Stack>
  );
};

export default ChequeTerceroRechazoRecargoRow;
